import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { Braces, Mail } from 'lucide-react';
import { PropsWithChildren, ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams, type To } from 'react-router-dom';
import type { MessengerPageSummary } from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import {
  API_INTEGRATION_SHEET,
  ApiIntegrationSheet,
} from '@/Domain/components/Integration/ApiIntegration/ApiIntegration.sheet';
import { DiscordSheet } from '@/Domain/components/Integration/Discord';
import { DrupalSheet } from '@/Domain/components/Integration/Drupal';
import { EmailSheet } from '@/Domain/components/Integration/Email';
import {
  EMAIL_FORWARDING_POLL_INTERVAL_MS,
  EMAIL_QUERY_KEY,
} from '@/Domain/components/Integration/Email/email.const';
import type { EmailIntegrationResponse } from '@/Domain/components/Integration/Email/email.types';
import { HubspotSheet } from '@/Domain/components/Integration/Hubspot';
import { InstagramSheet } from '@/Domain/components/Integration/Instagram';
import { IntercomSheet } from '@/Domain/components/Integration/Intercom';
import { McpIntegrationSheet } from '@/Domain/components/Integration/McpIntegration/McpIntegration.sheet';
import { MessengerSheet } from '@/Domain/components/Integration/Messenger';
import { MetaCallbackListener } from '@/Domain/components/Integration/MetaCallbackListener/MetaCallbackListener';
import { ShopifySheet } from '@/Domain/components/Integration/Shopify';
import { SlackLegacySheet } from '@/Domain/components/Integration/SlackLegacy';
import { SlackOAuthCallbackListener } from '@/Domain/components/Integration/SlackOAuthCallbackListener/SlackOAuthCallbackListener';
import { SlackV2Sheet } from '@/Domain/components/Integration/SlackV2';
import { TelegramSheet } from '@/Domain/components/Integration/Telegram';
import { WhatsAppPhoneNumbersManager } from '@/Domain/components/Integration/WhatsAppPhoneNumbers/WhatsAppPhoneNumbersManager';
import { ZendeskSheet } from '@/Domain/components/Integration/Zendesk';
import { useIntegrationOAuthCallback } from '@/Domain/components/Integration/useIntegrationOAuthCallback';
import { type Integrations } from '@/Domain/components/KnowledgeBaseArticles/types';
import { useTrackPageVisit } from '@/Domain/components/OnboardingProgress';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Skeleton } from '@/Domain/components/Shared/Skeleton/Skeleton';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { DiscordIcon } from '@/Domain/components/icons/Discord.icon';
import { DrupalIcon } from '@/Domain/components/icons/Drupal.icon';
import { HubSpotIcon } from '@/Domain/components/icons/HubSpot.icon';
import { InstagramIcon } from '@/Domain/components/icons/Instagram.icon';
import { IntercomIcon } from '@/Domain/components/icons/Intercom.icon';
import { McpIcon } from '@/Domain/components/icons/Mcp.icon';
import { MessengerIcon } from '@/Domain/components/icons/Messenger.icon';
import { ShopifyIcon } from '@/Domain/components/icons/Shopify.icon';
import { SlackIcon } from '@/Domain/components/icons/Slack.icon';
import { TelegramIcon } from '@/Domain/components/icons/Telegram.icon';
import { WhatsAppIcon } from '@/Domain/components/icons/WhatsApp.icon';
import { ZendeskIcon } from '@/Domain/components/icons/Zendesk.icon';
import { PostHogEventEnum, type EmailSheetSource } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useHubSpotTokenStatus } from '@/Domain/hooks/Integration/useHubSpotTokenStatus.hook';
import { useWhatsAppChannelHealthToast } from '@/Domain/hooks/Integration/useWhatsAppChannelHealthToast';
import { useGetSubscription } from '@/Domain/hooks/Subscription/useGetSubscription.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';
import {
  CHANNEL_API,
  CHANNEL_DISCORD,
  CHANNEL_EMAIL,
  CHANNEL_HUBSPOT,
  CHANNEL_INTERCOM,
  CHANNEL_MCP,
  CHANNEL_MESSENGER,
  CHANNEL_SHOPIFY,
  CHANNEL_SLACK,
  CHANNEL_TELEGRAM,
  CHANNEL_INSTAGRAM,
  CHANNEL_WHATSAPP,
  CHANNEL_ZENDESK,
  OPEN_MODAL_QUERY_PARAM,
} from '@/UI/GettingStarted/components/onboardingOptions';

interface TileProps {
  title: string;
  description: string;
  disabled?: boolean;
  className?: string;
  icon: JSX.Element;
  onClick?: () => void;
  to?: To;
  tier?: string;
  testId?: string;
  badge?: ReactNode;
}

type IntegrationName =
  | 'messenger'
  | 'instagram'
  | 'whatsapp'
  | 'telegram'
  | 'discord'
  | 'slack'
  | 'slackv2'
  | 'hubspot'
  | 'intercom'
  | 'drupal'
  | 'zendesk'
  | 'email'
  | 'shopify'
  | 'api'
  | 'mcp';

const Tile = ({
  title,
  description,
  disabled = false,
  icon,
  children,
  className,
  onClick,
  tier,
  testId,
  badge,
}: PropsWithChildren<TileProps>): JSX.Element => (
  <div
    role="button"
    data-testid={testId}
    tabIndex={disabled ? -1 : 0}
    className={classNames(
      'flex h-full flex-col gap-7 rounded-xl border border-border bg-card p-6 transition-colors',
      disabled ? 'bg-accent' : 'cursor-pointer hover:bg-accent hover:border-foreground-subtle',
      className,
    )}
    onClick={!disabled ? onClick : undefined}
    onKeyDown={(event) => {
      if (!disabled && onClick && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        onClick();
      }
    }}
  >
    <header className="flex items-center justify-between gap-1">
      <section className="flex items-center gap-4">
        <div className="flex size-10 items-center rounded-lg">{icon}</div>
      </section>

      {badge}

      {disabled && (
        <span className="text-xs text-foreground-subtle">
          <Link className="font-medium text-foreground" to="../subscription">
            {tier}
          </Link>{' '}
          required
        </span>
      )}
    </header>

    <section className="space-y-1.5">
      <p
        className={classNames('font-semibold', {
          'text-foreground-subtle': disabled,
          'text-foreground': !disabled,
        })}
      >
        {title}
      </p>
      <p
        className={classNames('text-sm line-clamp-2 min-h-10', {
          'text-foreground-subtle': disabled,
          'text-muted-foreground': !disabled,
        })}
      >
        {description}
      </p>
    </section>

    {children}
  </div>
);

// Mirrors the real Tile card structure so the loading state lays out identically
// to the loaded grid (icon + title + two description lines) instead of collapsing.
const SkeletonTile = (): JSX.Element => (
  <div
    className="flex h-full flex-col gap-7 rounded-xl border border-border bg-card p-6"
    data-testid="integration-tile-skeleton"
  >
    <Skeleton className="size-10 rounded-lg" />
    <div className="space-y-2">
      <Skeleton className="h-5 w-28" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
    </div>
  </div>
);

// Grid order, read left to right. API and MCP live here rather than as trailing
// one-off tiles so the sequence is expressed in one place.
const INTEGRATIONS: Array<{
  name: IntegrationName;
  Icon: any;
  /** Monochrome icons dim when the tile is locked; brand marks keep their colour. */
  tintIcon?: boolean;
}> = [
  { name: 'whatsapp', Icon: WhatsAppIcon },
  { name: 'instagram', Icon: InstagramIcon },
  { name: 'discord', Icon: DiscordIcon },
  { name: 'email', Icon: Mail, tintIcon: true },
  { name: 'hubspot', Icon: HubSpotIcon },
  { name: 'intercom', Icon: IntercomIcon },
  { name: 'messenger', Icon: MessengerIcon },
  { name: 'telegram', Icon: TelegramIcon },
  { name: 'shopify', Icon: ShopifyIcon },
  // Only rendered for the few accounts still on the legacy Slack app; when it
  // shows, it sits next to its replacement rather than adrift at the end.
  { name: 'slack', Icon: SlackIcon },
  { name: 'slackv2', Icon: SlackIcon },
  { name: 'api', Icon: Braces, tintIcon: true },
  { name: 'mcp', Icon: McpIcon, tintIcon: true },
  { name: 'zendesk', Icon: ZendeskIcon },
  { name: 'drupal', Icon: DrupalIcon },
];

export default function IntegrationsPage() {
  // `subscription_page` carries the complete tier-label set (Starter, Free,
  // …); the `integrations` namespace only has a partial set, so tier gating
  // labels there fall back to the raw key (e.g. "tiers.starter").
  const { t } = useTranslation(['integrations', 'subscription_page']);
  useTrackPageVisit('external_apps');
  const { toggleModal } = useModalStore();
  const { capture } = usePostHogAnalytics();
  const [searchParams, setSearchParams] = useSearchParams();

  const { isActivated, getConfig, isFetched: isSubscriptionFetched } = useSubscription();
  const subscriptionQuery = useGetSubscription();
  const integrationsData = useQuery<Integrations>({ queryKey: ['integrations'] });

  // Surface a toast when the connected WhatsApp channel needs the user's
  // attention (e.g. Meta display-name approval), pointing to the WhatsApp modal.
  // Gate on the plan allowing WhatsApp so non-WhatsApp accounts don't hit the
  // status endpoint (a live Meta ping for connected accounts) just for a toast.
  const whatsappAllowed = Boolean(
    (getConfig('allowed_integrations') as Record<string, boolean> | undefined)?.whatsapp,
  );
  useWhatsAppChannelHealthToast(whatsappAllowed);

  // Until /allowed_features and the integrations query resolve, getConfig
  // returns {} so every tile reads as disabled and flashes a "{tier} required"
  // gating label (with a raw i18n key for the tier). Hold the grid as skeletons
  // until both are fetched so that pre-load flash never renders.
  const isIntegrationsReady = isSubscriptionFetched && integrationsData.isFetched;

  const [openSheet, setOpenSheet] = useState<IntegrationName | null>(null);
  const closeSheet = useCallback(() => setOpenSheet(null), []);

  // A marketplace partner install ends back inside HubSpot before the bot email
  // is configured; the backend marker drives this finish-setup prompt.
  const hubspotTokenStatus = useHubSpotTokenStatus();
  const showHubspotFinishSetup = Boolean(hubspotTokenStatus.data?.partner_install_pending_setup);

  // Reopen the sheet + toast when an OAuth full-page redirect returns here.
  const openHubspotSheet = useCallback(() => setOpenSheet('hubspot'), []);
  const openIntercomSheet = useCallback(() => setOpenSheet('intercom'), []);
  const openDiscordSheet = useCallback(() => setOpenSheet('discord'), []);
  useIntegrationOAuthCallback('hubspot', openHubspotSheet);
  useIntegrationOAuthCallback('intercom', openIntercomSheet);
  useIntegrationOAuthCallback('discord', openDiscordSheet);

  // SubscriptionProvider's data is empty `{}` until /allowed_features resolves,
  // so default to an empty record to avoid `Cannot read properties of undefined`
  // crashes on first paint.
  const integrations =
    (getConfig('allowed_integrations') as Record<string, boolean> | undefined) ?? {};
  // Instagram reaches here only when the popup was blocked and the connect finished in this
  // tab; the popup flow reports through MetaCallbackListener instead.
  const openInstagramSheet = useCallback(() => setOpenSheet('instagram'), []);
  useIntegrationOAuthCallback('instagram', openInstagramSheet);
  const emailAllowed = Boolean(integrations.email);

  const isMcpIntegrationDisabled =
    !isActivated('integrations.mcp') &&
    !(
      subscriptionQuery?.data?.subscription?.trial_expired === true &&
      !subscriptionQuery?.data?.subscription?.payment_failed
    );

  const isDisabled = useCallback(
    (integration: string) => !integrations[integration],
    [integrations],
  );

  // Shopify and API are never tier-locked, and MCP is gated on activation rather
  // than on the tier map every other tile reads.
  const isTileDisabled = useCallback(
    (name: IntegrationName) => {
      if (name === 'shopify' || name === 'api') return false;
      if (name === 'mcp') return isMcpIntegrationDisabled;
      return isDisabled(name);
    },
    [isMcpIntegrationDisabled, isDisabled],
  );

  // A Messenger connect that stopped at the page pick is invisible outside the sheet,
  // which is how one merchant re-ran OAuth eight times instead of confirming.
  const { data: messengerPendingPages } = useQuery<MessengerPageSummary[]>({
    queryKey: [QueryKey.integration.messengerPages],
    enabled: isIntegrationsReady && !isDisabled('messenger'),
  });
  const hasPendingMessengerPages = (messengerPendingPages?.length ?? 0) > 0;

  const { data: emailIntegration } = useQuery<EmailIntegrationResponse>({
    queryKey: EMAIL_QUERY_KEY,
    enabled: isIntegrationsReady && emailAllowed,
    refetchInterval: (query) => {
      const params = query.state.data?.params;
      return params?.email_bot_active && !params.forwarding_active
        ? EMAIL_FORWARDING_POLL_INTERVAL_MS
        : false;
    },
  });
  const emailEnabledButNotReceiving =
    Boolean(emailIntegration?.params?.email_bot_active) &&
    !emailIntegration?.params?.forwarding_active;

  const captureEmailSheetOpened = useCallback(
    (source: EmailSheetSource) => capture(PostHogEventEnum.EmailIntegrationSheetOpened, { source }),
    [capture],
  );

  const handleTileClick = useCallback(
    (name: IntegrationName) => {
      if (name === 'slack' || name === 'slackv2') {
        capture(PostHogEventEnum.SlackIntegrationModalOpened, { integration_type: name });
      }
      if (name === 'messenger') {
        capture(PostHogEventEnum.MessengerConnectModalOpened);
      }
      if (name === 'instagram') {
        capture(PostHogEventEnum.InstagramConnectModalOpened);
      }
      if (name === 'email') {
        captureEmailSheetOpened('tile');
      }
      // Modal-store-driven sheets: they render off the modal store, never off
      // `openSheet`, so routing them through setOpenSheet is a dead click.
      if (name === 'whatsapp') {
        // Owns its own Sheet + 2-step setup + OAuth flow.
        toggleModal(MODAL_NAMES.WHATSAPP_PHONE_NUMBERS_SHEET);
        return;
      }
      if (name === 'api') {
        toggleModal(API_INTEGRATION_SHEET);
        return;
      }
      if (name === 'mcp') {
        toggleModal(MODAL_NAMES.MCP_INTEGRATION_SHEET);
        return;
      }
      setOpenSheet(name);
    },
    [capture, captureEmailSheetOpened, toggleModal],
  );

  const openParam = searchParams.get(OPEN_MODAL_QUERY_PARAM);
  useEffect(() => {
    if (!openParam || !isIntegrationsReady) return;
    switch (openParam) {
      case CHANNEL_SHOPIFY:
        setOpenSheet('shopify');
        break;
      case CHANNEL_HUBSPOT:
        setOpenSheet('hubspot');
        break;
      case CHANNEL_INTERCOM:
        setOpenSheet('intercom');
        break;
      case CHANNEL_WHATSAPP:
        toggleModal(MODAL_NAMES.WHATSAPP_PHONE_NUMBERS_SHEET);
        break;
      case CHANNEL_MESSENGER:
        capture(PostHogEventEnum.MessengerConnectModalOpened);
        setOpenSheet('messenger');
        break;
      case CHANNEL_INSTAGRAM:
        capture(PostHogEventEnum.InstagramConnectModalOpened);
        setOpenSheet('instagram');
        break;
      case CHANNEL_TELEGRAM:
        setOpenSheet('telegram');
        break;
      case CHANNEL_DISCORD:
        setOpenSheet('discord');
        break;
      case CHANNEL_SLACK:
        // Legacy "slack" tile only renders when old_integration_is_active; default to v2.
        capture(PostHogEventEnum.SlackIntegrationModalOpened, { integration_type: 'slackv2' });
        setOpenSheet('slackv2');
        break;
      case CHANNEL_ZENDESK:
        setOpenSheet('zendesk');
        break;
      case CHANNEL_EMAIL:
        if (emailAllowed) {
          captureEmailSheetOpened('deep_link');
          setOpenSheet('email');
        }
        break;
      case CHANNEL_MCP:
        toggleModal(MODAL_NAMES.MCP_INTEGRATION_SHEET);
        break;
      case CHANNEL_API:
        toggleModal(API_INTEGRATION_SHEET);
        break;
      default:
        break;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(OPEN_MODAL_QUERY_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [
    openParam,
    isIntegrationsReady,
    emailAllowed,
    toggleModal,
    capture,
    captureEmailSheetOpened,
    setSearchParams,
  ]);

  return (
    <div className="w-full max-w-7xl mx-auto">
      <h1 className="text-lg font-semibold mb-6">{t('title')}</h1>

      {showHubspotFinishSetup && (
        <div
          data-testid="hubspot-finish-setup-banner"
          className="mb-6 flex flex-wrap items-center gap-4 rounded-xl border border-info bg-info-muted p-4"
        >
          <HubSpotIcon className="size-8 shrink-0" />
          <div className="min-w-0 flex-1 space-y-0.5">
            <p className="font-semibold text-foreground">{t('hubspot_finish_setup.title')}</p>
            <p className="text-sm text-muted-foreground">{t('hubspot_finish_setup.description')}</p>
          </div>
          <button
            type="button"
            data-testid="hubspot-finish-setup-action"
            className="shrink-0 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            onClick={openHubspotSheet}
          >
            {t('hubspot_finish_setup.action')}
          </button>
        </div>
      )}

      <div className="flex flex-col gap-10">
        <div className="border-0">
          <div className="border-0 rounded-xl relative p-0">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {!isIntegrationsReady ? (
                Array.from({ length: 12 }).map((_, i) => <SkeletonTile key={i} />)
              ) : (
                <>
                  {INTEGRATIONS.filter(
                    ({ name }) =>
                      !(
                        name === 'slack' &&
                        !integrationsData?.data?.slack?.old_integration_is_active
                      ) &&
                      // Email is launch-flagged: hide it when disabled instead of showing a locked "tier required" tile like the others.
                      !(name === 'email' && isDisabled('email')),
                  ).map(({ name, Icon: IntegrationIcon, tintIcon }): JSX.Element => {
                    const disabled = isTileDisabled(name);

                    return (
                      <Tile
                        key={name}
                        testId={`integration-tile-${name}`}
                        title={t(`other.${name}`)}
                        description={t(`other_description.${name}`)}
                        icon={
                          <IntegrationIcon
                            className={classNames('size-8', {
                              'text-foreground-subtle': tintIcon && disabled,
                              'text-foreground': tintIcon && !disabled,
                            })}
                          />
                        }
                        disabled={disabled}
                        tier={
                          disabled
                            ? t(`subscription_page:tiers.${integrationsData?.data?.tiers[name]}`)
                            : undefined
                        }
                        badge={
                          // Deliberately unguarded by connected state (unlike the sheet's
                          // IntegrationStatusBadge): an unconfirmed pick is worth surfacing even on a
                          // live integration, and the page has no connected signal without a second request.
                          name === 'messenger' && hasPendingMessengerPages ? (
                            <Badge
                              variant="warning"
                              size="sm"
                              className="px-2"
                              data-testid="integration-tile-messenger-pending"
                            >
                              {t('integrations:status.finish_setup')}
                            </Badge>
                          ) : name === 'email' && emailEnabledButNotReceiving ? (
                            <Badge
                              variant="warning"
                              size="sm"
                              className="px-2"
                              data-testid="integration-tile-email-not-receiving"
                            >
                              {t('integrations:status.finish_setup')}
                            </Badge>
                          ) : undefined
                        }
                        onClick={() => handleTileClick(name)}
                      />
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <ApiIntegrationSheet />
      <McpIntegrationSheet />
      <WhatsAppPhoneNumbersManager />
      <DiscordSheet open={openSheet === 'discord'} onOpenChange={(v) => !v && closeSheet()} />
      <SlackLegacySheet open={openSheet === 'slack'} onOpenChange={(v) => !v && closeSheet()} />
      <SlackV2Sheet open={openSheet === 'slackv2'} onOpenChange={(v) => !v && closeSheet()} />
      <TelegramSheet open={openSheet === 'telegram'} onOpenChange={(v) => !v && closeSheet()} />
      <IntercomSheet open={openSheet === 'intercom'} onOpenChange={(v) => !v && closeSheet()} />
      <HubspotSheet open={openSheet === 'hubspot'} onOpenChange={(v) => !v && closeSheet()} />
      <MessengerSheet open={openSheet === 'messenger'} onOpenChange={(v) => !v && closeSheet()} />
      <InstagramSheet open={openSheet === 'instagram'} onOpenChange={(v) => !v && closeSheet()} />
      <DrupalSheet open={openSheet === 'drupal'} onOpenChange={(v) => !v && closeSheet()} />
      <ZendeskSheet open={openSheet === 'zendesk'} onOpenChange={(v) => !v && closeSheet()} />
      <EmailSheet open={openSheet === 'email'} onOpenChange={(v) => !v && closeSheet()} />
      <ShopifySheet open={openSheet === 'shopify'} onOpenChange={(v) => !v && closeSheet()} />
      <MetaCallbackListener />
      <SlackOAuthCallbackListener />
    </div>
  );
}
